import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
	ChipModelV2,
	ContentModalService,
	MsiContentModalRef,
	ContentModalModule,
	AutocompleteV2Module,
} from '@msi/cobalt';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ViewModel } from 'src/app/interfaces/views.interface';
import { CcAdminApiService } from 'src/app/services/ccAdmin/cc-admin-api.service';
import { ViewsService } from 'src/app/services/views/views.service';
import { ViewConfigModalComponent } from '../view-config-modal/view-config-modal.component';
import { TooltipModule } from '@msi/cobalt/tooltip';

@Component({
	selector: 'app-select-workspace-modal',
	templateUrl: './select-workspace-modal.component.html',
	styleUrls: ['./select-workspace-modal.component.scss'],
	standalone: true,
	imports: [ContentModalModule, AutocompleteV2Module, TooltipModule],
})
export class SelectWorkspaceModalComponent implements OnInit, OnDestroy {
	public readonly atLeastOneWorkspaceRequiredTooltip = $localize`You need at least one Workspace to create a Workflow. Please create a Workspace first.`;
	public readonly selectAWorkspaceTooltip = $localize`Please select a Workspace to create a Workflow in.`;
	/**
	 * Workspace list
	 */
	public workspaces: ChipModelV2[] = [];
	public workspacesLoading = true;
	public selectedWorkspaceId = null;
	public cancelButtonDisabled = false;

	canUserEditWorkflow: boolean;
	canUserCreateNewWorkspaces: boolean;

	private destroy: Subject<void> = new Subject();

	constructor(
		private viewsService: ViewsService,
		private router: Router,
		private contentModalService: ContentModalService,
		private ccAdminApiService: CcAdminApiService,
		public modalRef: MsiContentModalRef
	) {}

	ngOnInit(): void {
		this.ccAdminApiService.graphInitialized$.pipe(take(1)).subscribe(() => {
			this.canUserEditWorkflow =
				this.ccAdminApiService.user.canEditWorkflows || this.ccAdminApiService.user.canEditAnyWorkflow;
			this.canUserCreateNewWorkspaces = this.ccAdminApiService.user.canEditAnyWorkspace;

			this.viewsService.views$.pipe(takeUntil(this.destroy)).subscribe((workspaces: ViewModel[]) => {
				this.workspaces = workspaces
					.filter(w => this.ccAdminApiService.user.canEditWorkflowsInWorkspace(w))
					.map(workspace => ({
						id: workspace.id,
						name: workspace.name,
						value: workspace.name,
						isSelected: workspace.id === this.selectedWorkspaceId,
					}));
				this.workspacesLoading = false;
			});
			// Don't let users press cancel if they are coming directly through the URL otherwise they will be sent to a new Workflow with no dedicated workspace
			this.cancelButtonDisabled = this.modalRef.getData()?.cancelButtonDisabled ?? false;
		});
	}

	ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	/**
	 * Opens the new workspace config modal
	 */
	openNewWorkspaceConfigModal() {
		this.contentModalService.open(ViewConfigModalComponent, {
			data: { viewId: null, selectedMediaIndex: 0 },
		});
	}

	selectWorkspace(workspaceId: string) {
		this.selectedWorkspaceId = this.workspaces.find(w => w.id === workspaceId)?.id;
	}

	cancelClickHandler() {
		this.modalRef.close();
	}

	saveClickHandler() {
		if (!this.selectedWorkspaceId) return;
		this.viewsService.setActiveView(this.selectedWorkspaceId);
		this.modalRef.close();
		this.router.navigate(['workflows', 'new'], { queryParams: { workspaceId: this.selectedWorkspaceId } });
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
